import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

/**
 * One-shot "You're verified!" moment. When a user returns from the email
 * confirm link, VerifyEmailPage stamps sessionStorage `iw_just_verified=1`
 * and redirects into the app. This component (mounted in AppShell once the
 * user is present) detects that flag on the next app load, fires a confetti
 * burst + a celebratory toast, then clears the flag so it never repeats.
 */
const COLORS = ["#22C55E", "#10B981", "#34D399", "#60A5FA", "#FBBF24", "#A78BFA", "#F472B6"];

export default function VerifiedCelebration() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let flagged = false;
    try {
      flagged = sessionStorage.getItem("iw_just_verified") === "1";
      if (flagged) sessionStorage.removeItem("iw_just_verified");
    } catch { /* storage blocked */ }
    if (!flagged) return;

    setShow(true);
    toast.success("You're verified! 🎉", {
      description: "Your email is confirmed and your account is fully unlocked.",
      duration: 5000,
    });
    const t = setTimeout(() => setShow(false), 3200);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const pieces = Array.from({ length: 44 });
  return (
    <div
      className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden"
      data-testid="verified-celebration"
      aria-hidden="true"
    >
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const dur = 1.8 + Math.random() * 1.2;
        const size = 7 + Math.random() * 8;
        const color = COLORS[i % COLORS.length];
        return (
          <span
            key={i}
            className="confetti-piece absolute"
            style={{
              left: `${left}%`,
              top: "-5%",
              width: `${size}px`,
              height: `${size * 0.5}px`,
              background: color,
              animationDelay: `${delay}s`,
              animationDuration: `${dur}s`,
            }}
          />
        );
      })}
      <div className="absolute inset-x-0 top-24 flex justify-center">
        <div className="flex items-center gap-2.5 rounded-2xl border border-emerald-400/40 bg-emerald-500/15 backdrop-blur-md px-5 py-3 shadow-2xl animate-[fadeInUp_0.4s_ease-out]">
          <CheckCircle2 size={22} className="text-emerald-300" />
          <div>
            <div className="text-white font-bold text-sm">You're all set!</div>
            <div className="text-emerald-100/70 text-xs">Email confirmed &amp; account unlocked.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
